import storage from "local-storage-fallback";
import { decode } from "jsonwebtoken";
import { call, put, delay, putResolve, select } from "redux-saga/effects";
import { putWait } from "redux-saga-callback";
import { push } from "connected-react-router";
import {
  doSuccessLogin,
  doErrorLogin,
  doChangeResendSMSStatus,
  doAddIdentifier,
  doUnsuccessfulEncryptedLogin,
} from "../actions/session";
import { doFetchRechargeValues, doResetRechargeValues } from "../actions/rechargeValue";
import { doChangeNewTvRecharge } from "../actions/tvRecharge";
import { doChangeNewRecharge, doResetNewRecharge, doSaveRecharges } from "../actions/recharge";
import { doResetScheduleRecharge, doSaveScheduledRecharges } from "../actions/scheduledRecharge";
import { fetchScheduledRecharges } from "../api/scheduledRecharge";
import {
  doShowLoading,
  doHideLoading,
  doShowError,
  doHideModal,
  doShowModal,
  doShowSnackbar,
} from "../actions/ui";
import { requestSMS, requestLogin, requestCpfLogin } from "../api/session";
import channels from "../enum/channels";
import modals from "../enum/modals";
import smsStatuses from "../enum/smsStatuses";
import segments from "../enum/segments";
import { handleSendBrowserInfo } from "./fraudAnalysis";
import { doSaveFingerprint } from "../actions/fraudAnalysis";
import { getDeviceFingerprint } from "../lib/fraudAnalysis";
import {
  getChannel,
  getDayDate,
  getScheduleDayDate,
  isClaroFlex,
  isElegibleTagsChannel,
  logout,
  setDigisessionSession,
} from "../lib/utils";
import { doEncryptedLoginRequest } from "../actions/analytics";
import { fetchCustomer, fetchPaymentMethods, requestEventAbandonedCart } from "../api/customer";
import { doResetCustomer, doSaveCustomer, doSaveCheckoutSessionData } from "../actions/customer";
import { customers } from "../enum/customers";
import { doFetchFeatures } from "../actions/feature";
import { handlePriorityFetchFeature } from "./feature";
import { getNewRecharge } from "../selectors/recharge";
import { getRechargeValues } from "../selectors/rechargeValue";
import { getAvailableBonus } from "../selectors/customer";
import { createCheckoutCoreSession, getSmartcheckoutUrlV2 } from "../services/smartcheckout";
import { setSessionLoopId } from "../services/loopEvents";
import { doResetRechargeResume, doSaveRechargeResume } from "../actions/rechargeResume";
import { fetchRecharges } from "../api/recharge";
import { getCardsPerType } from "../selectors/paymentMethod";
import { doSavePaymentMethods, doFetchPaymentMethods } from "../actions/paymentMethod";
import payments from "../enum/paymentMethods";
import { homeChannels } from "../enum/homeChannels";
import tokenOrigins from "../enum/tokenOrigins";
import { isFeatureEnabled } from "../selectors/feature";
import { isEligibleForRechargeValueSelect } from "../selectors/campaignOffers";
import { doFetchOfferCatalog, doResetOfferCatalog } from "../actions/offerCatalog";
import menuPaths from "../enum/menuPaths";

function* handleRequestSMS(action) {
  yield put(doShowLoading());

  const { payload } = action;
  try {
    yield put(doAddIdentifier(payload.msisdn));
    yield call(requestSMS, payload);

    if (payload.target) yield put(push(payload.target));
  } catch (e) {
    yield put(doShowError("Erro ao enviar SMS.", e));
  } finally {
    yield put(doHideLoading());
  }
}

function* handleRequestResendSMS(action) {
  yield put(doChangeResendSMSStatus(smsStatuses.SENDING));

  const { payload } = action;

  try {
    if (payload.showLoading) yield put(doShowLoading());
    yield put(doAddIdentifier(payload.msisdn));
    yield call(requestSMS, payload);
  } catch (e) {
    yield put(doShowError("Erro ao enviar SMS.", e));
  } finally {
    yield delay(1000);
    yield put(doChangeResendSMSStatus(smsStatuses.DONE));
    if (payload.showLoading) yield put(doHideLoading());
    yield put(doShowSnackbar("success", "Código de confirmação reenviado com sucesso."));
  }
}

function getNextRouteAfterLogin({ loginAction, customerStatus, newLoginForm }) {
  if (loginAction === "manage") {
    return { type: "MANAGE" };
  }

  if (loginAction === "schedule") {
    return { type: "SCHEDULE" };
  }

  if (loginAction === "loginWithRechargeValue" && newLoginForm) {
    return { type: "LOGIN_WITH_RECHARGE_VALUE" };
  }

  if (customerStatus === customers.CREATED) {
    return { type: "NEW_CUSTOMER" };
  }

  return { type: "HOME" };
}

function buildSessionData({
  token,
  identifier,
  segment,
  channelPath,
  geoInformations,
  rechargeValue,
}) {
  return {
    token,
    identifier,
    segment,
    channelPath,
    sourceIp: geoInformations?.IPv4,
    countryOrigin: geoInformations?.country_code,
    rechargeValue,
  };
}

function persistSession(session) {
  storage.setItem("identifier", session.identifier);
  storage.setItem("segment", session.segment);

  sessionStorage.setItem("token", session.token);
  sessionStorage.setItem("channelPath", session.channelPath);
  sessionStorage.setItem("rechargeValue", session.rechargeValue);
  sessionStorage.removeItem("origin");

  if (session.sourceIp) {
    sessionStorage.setItem("sourceIp", session.sourceIp);
  }

  if (session.countryOrigin) {
    sessionStorage.setItem("countryOrigin", session.countryOrigin);
  }
}

function* resetLoginState() {
  storage.clear();
  yield put(doResetCustomer());
  yield put(doResetRechargeValues());
  yield put(doResetNewRecharge());
  yield put(doResetRechargeResume());
  yield put(doResetScheduleRecharge());
  yield put(doResetOfferCatalog());
}

function* navigateAfterLogin(decision) {
  switch (decision.type) {
    case "MANAGE": {
      const { data } = yield call(fetchScheduledRecharges);
      yield putResolve(doSaveScheduledRecharges(data));
      yield put(push("gerenciar-programadas"));
      break;
    }
    case "NEW_CUSTOMER":
      yield put(push("numero"));
      break;
    case "LOGIN_WITH_RECHARGE_VALUE":
      yield put(push("numero"));
      break;
    case "HOME":
    default:
      yield put(push("home"));
      break;
  }
}

function* handleRequestLogin(action) {
  const { payload } = action;
  if (payload.loading) yield put(doShowLoading());

  try {
    yield* resetLoginState();

    const [channelPath] = payload.channel;

    const geoInformations = null;

    const { data } = yield call(requestLogin, payload);
    const session = buildSessionData({
      token: data.id,
      identifier: payload.msisdn,
      segment: data.segment,
      channelPath,
      geoInformations,
      rechargeValue: payload.loginValue,
    });
    persistSession(session);

    const { data: customer } = yield call(fetchCustomer, data.identifier);
    yield put(doSuccessLogin({ token: data.id, identifier: payload.msisdn }));
    yield put(doSaveCustomer(customer));

    yield call(handlePriorityFetchFeature);
    yield putResolve(doFetchFeatures());
    yield putResolve(doFetchRechargeValues(true));
    yield putResolve(doFetchPaymentMethods(null, true));

    const maintenance = yield select(state => isFeatureEnabled(state, "maintenance"));
    if (maintenance) {
      yield put(push("maintenance"));
      return;
    }

    const newLoginForm = yield select(state => isFeatureEnabled(state, "loginWithValue"));

    const { data: paymentMethods } = yield call(fetchPaymentMethods);
    yield call(requestEventAbandonedCart, {
      customer: {
        id: customer.id,
        credit_cards: getCardsPerType(paymentMethods, "credit"),
      },
    });

    const decision = getNextRouteAfterLogin({
      loginAction: payload.loginAction,
      customerStatus: customer.status,
      newLoginForm,
    });

    yield* navigateAfterLogin(decision);
  } catch (e) {
    storage.clear();

    yield put(doErrorLogin());
    if (e.response && e.response.status === 400) {
      yield put(doShowModal(modals.INVALID_AUTHENTICATION_TOKEN));
    } else {
      yield put(push(payload.origin));
      yield delay(250);
      yield put(doShowError("Erro ao logar. Tente novamente.", e));
    }
  } finally {
    if (payload.loading === true) yield put(doHideLoading());
  }
}

function* handleRequestLoginDeviceToken() {
  try {
    const fingerprint = yield getDeviceFingerprint();

    yield put(doSaveFingerprint(fingerprint));

    if (process.env.REACT_APP_FRAUD_ANALYSIS_FLAG === "true") {
      yield handleSendBrowserInfo();
    }

    return null;
  } catch (e) {
    return null;
  }
}

function* handleRequestCpfLogin(action) {
  const { payload } = action;
  if (payload.loading === true) yield put(doShowLoading());

  try {
    storage.setItem("channelPath", "tv");
    storage.setItem("channel", channels[storage.getItem("channelPath")]);

    const { data } = yield call(requestCpfLogin, payload);
    storage.setItem("token", data.id);
    storage.setItem("identifier", payload.data.identifier);

    yield put(doSuccessLogin({ token: data.id, identifier: payload.data.identifier }));
    yield put(doChangeNewTvRecharge("target", payload.data.identifier));

    yield put(push(`../${storage.getItem("channelPath")}/home`));
  } catch (e) {
    storage.clear();

    yield put(push(payload.origin));
    yield delay(250);
    yield put(doShowError("Erro ao logar. Tente novamente.", e));
  } finally {
    if (payload.loading === true) yield put(doHideLoading());
  }
}

function* handleRequestEncryptedLogin(action) {
  const { payload } = action;

  try {
    storage.clear();
    yield put(doResetCustomer());

    const [channelPath, channelName] = payload.channel;
    const decodedPayload = decode(payload.data);

    const geoInformations = null;

    yield put(doResetRechargeValues());
    yield put(doResetNewRecharge());

    sessionStorage.setItem("channelPath", channelPath);
    sessionStorage.setItem("channel", channelName);
    sessionStorage.setItem("canRequestPixBundle", true);
    sessionStorage.setItem("canRequestDataBundle", true);
    sessionStorage.removeItem("origin");

    const { data } = yield call(requestLogin, payload);
    const recipient = data.recipient || data.identifier;
    const channel = getChannel();

    storage.setItem("identifier", data.identifier);
    sessionStorage.setItem("token", data.id);
    sessionStorage.setItem("partnerExternalId", data.partnerExternalId);
    storage.setItem("value", data.value);
    storage.setItem("segment", data.segment);
    storage.setItem("encryptedWithRecipient", recipient);
    storage.setItem("loginCardToken", data.token);
    sessionStorage.setItem("channel", data.portal || channelName);
    sessionStorage.setItem("sourceIp", geoInformations?.IPv4);
    sessionStorage.setItem("countryOrigin", geoInformations?.country_code);
    if (decodedPayload.origin) {
      sessionStorage.setItem("origin", decodedPayload.origin);
    }

    yield put(
      doSuccessLogin({
        token: data.id,
        partnerExternalId: data.partnerExternalId,
        identifier: data.identifier,
        recipient: data.recipient ? data.recipient : data.identifier,
        loginRechargeValue: data.value * 100,
        segment: data.segment,
        loginCardToken: data.token,
        channel: data.portal || channelName,
        channelPath,
      })
    );

    const { data: customer } = yield call(fetchCustomer, data.identifier);
    yield put(doSaveCustomer(customer));

    const { data: paymentMethods } = yield call(fetchPaymentMethods);
    yield put(doSavePaymentMethods(paymentMethods));

    yield call(requestEventAbandonedCart, {
      customer: {
        id: customer.id,
        credit_cards: getCardsPerType(paymentMethods, payments.CREDITO),
      },
    });

    if (isElegibleTagsChannel(channelName)) {
      if (decodedPayload.tid) {
        sessionStorage.setItem("tid", decodedPayload.tid);
      }
      if (decodedPayload.sessionId) {
        sessionStorage.setItem("sessionId", decodedPayload.sessionId);
      }
      if (decodedPayload.offerId) {
        sessionStorage.setItem("offerId", decodedPayload.offerId);
      }
      if (decodedPayload.utm_content) {
        sessionStorage.setItem("utmContent", decodedPayload.utm_content);
      }
      if (decodedPayload.utm_medium) {
        sessionStorage.setItem("utmMedium", decodedPayload.utm_medium);
      }
      if (decodedPayload.utm_source) {
        sessionStorage.setItem("utmSource", decodedPayload.utm_source);
      }
      if (decodedPayload.utm_campaign) {
        sessionStorage.setItem("utmCampaign", decodedPayload.utm_campaign);
      }
    }

    if (
      decodedPayload.isPix ||
      decodedPayload.paymentMethodToRepeat === payments.PIX ||
      isClaroFlex(channel)
    ) {
      const selectedPaymentMethod = paymentMethods.find(
        paymentMethod => paymentMethod.type === payments.PIX
      );

      if (selectedPaymentMethod) {
        yield put(
          doChangeNewRecharge("paymentMethod", {
            type: selectedPaymentMethod.type,
            data: {},
            label: selectedPaymentMethod.label,
          })
        );
      }
    }

    try {
      const recharges = yield call(fetchRecharges);
      yield put(doSaveRecharges(recharges.data));
    } catch (e) {
      console.error("Erro ao carregar as recargas.", e);
    }

    const value = data.value * 100;

    const bonusText = yield select(state =>
      getAvailableBonus(
        value,
        state.customerState,
        null,
        null,
        null,
        channel,
        data.segment,
        data.identifier
      )
    );

    yield put(doSaveRechargeResume(value, bonusText));

    yield call(handlePriorityFetchFeature);
    yield putResolve(doFetchFeatures());

    const maintenance = yield select(state => isFeatureEnabled(state, "maintenance"));
    if (maintenance) {
      yield put(push("maintenance"));
      return;
    }

    const shouldGoToHome =
      homeChannels.includes(channelName) &&
      !decodedPayload.isPix &&
      !decodedPayload.goToScheduleRecharge &&
      !decodedPayload.lastRechargeRepeat;

    const isPOS = data.segment === segments.pos;

    if (isElegibleTagsChannel(channel)) {
      yield putWait(doFetchRechargeValues());

      if (decodedPayload.goToScheduleRecharge) {
        const todayDay = getDayDate();
        yield putResolve(doChangeNewRecharge("isScheduled", true));
        yield putResolve(doChangeNewRecharge("day", `${getScheduleDayDate(todayDay)}`));
        yield putResolve(doChangeNewRecharge("dayLabel", `Dia ${getScheduleDayDate(todayDay)}`));
      }

      yield put(
        doChangeNewRecharge("paymentMethod", {
          type: payments.CREDITO,
          data: {},
          label: "Cartão de Crédito",
        })
      );
      yield put(push("pagamento-cartao"));
    } else if (shouldGoToHome) {
      yield put(push("home"));
    } else {
      yield putWait(doFetchRechargeValues());

      if (decodedPayload.goToScheduleRecharge) {
        yield putResolve(doChangeNewRecharge("isScheduled", true));
      }

      const claroVipKnownOrigins = [
        tokenOrigins.CLARO_VIP_SMS_INCENTIVE,
        tokenOrigins.CLARO_VIP_SCHEDULE_MANAGER,
        tokenOrigins.CLARO_VIP_NEW_RECHARGE,
      ];

      if (
        decodedPayload.origin === tokenOrigins.CLARO_VIP_SMS_INCENTIVE &&
        !data.value &&
        channelName === channels["claro-vip"]
      ) {
        yield put(push("selecionar-valor"));
      } else if (
        decodedPayload.origin === tokenOrigins.CLARO_VIP_SCHEDULE_MANAGER &&
        channelName === channels["claro-vip"]
      ) {
        yield put(push("home"));
      } else if (
        decodedPayload.origin === tokenOrigins.CLARO_VIP_NEW_RECHARGE &&
        channelName === channels["claro-vip"]
      ) {
        yield put(push("numero"));
      } else if (
        decodedPayload.origin === tokenOrigins.CLARO_VIP_SMS_INCENTIVE &&
        data.value &&
        channelName === channels["claro-vip"]
      ) {
        const valueCentavos = data.value * 100;
        const rechargeValues = yield select(state => getRechargeValues(state));
        const matchingValue = rechargeValues.find(rv => rv.value === valueCentavos);

        if (!isEligibleForRechargeValueSelect(customer?.traits)) {
          yield put(push("selecionar-valor"));
        } else if (matchingValue) {
          try {
            const { data: checkoutData } = yield call(
              getSmartcheckoutUrlV2,
              data.identifier,
              channelName,
              recipient,
              matchingValue.id,
              decodedPayload.origin
            );
            yield put(doSaveRechargeResume(value, "", undefined, "selecionar-valor"));
            yield put(doSaveCheckoutSessionData({ checkoutData }));
            yield put(push("smartcheckout"));
          } catch (e) {
            yield put(push("numero"));
          }
        } else {
          yield put(push("numero"));
        }
      } else if (
        channelName === channels["claro-vip"] &&
        !claroVipKnownOrigins.includes(decodedPayload.origin)
      ) {
        yield put(push("numero"));
      } else if (
        !isPOS &&
        !decodedPayload.lastRechargeRepeat &&
        (channelName === channels.whatsapp || data.value)
      ) {
        const newRechargeValue = yield select(state => getNewRecharge(state));
        if (newRechargeValue.rechargeValue.isAvailable !== undefined) {
          storage.setItem("encryptedlogin", true);
        }
        yield put(push("numero"));
      } else if (decodedPayload.lastRechargeRepeat) {
        yield put(push("confirm-repeat"));
      } else {
        yield put(push("numero"));
      }
    }
    yield put(doEncryptedLoginRequest(channelName, "sucesso"));
  } catch (e) {
    yield put(
      doUnsuccessfulEncryptedLogin({
        channel: sessionStorage.getItem("channel"),
        channelPath: sessionStorage.getItem("channelPath"),
      })
    );
    yield put(
      push(`../${sessionStorage.getItem("channelPath")}/landing`, {
        showDelayedModal: true,
      })
    );
    yield put(doShowError("Erro ao logar. Tente novamente.", e));
    yield put(doEncryptedLoginRequest(payload.channel.channelName, "falha"));
  }
}

function* failDigisessionAuth(e) {
  yield put(
    doUnsuccessfulEncryptedLogin({
      channel: sessionStorage.getItem("channel"),
      channelPath: sessionStorage.getItem("channelPath"),
    })
  );
  yield put(
    push(`../${sessionStorage.getItem("channelPath")}/landing`, {
      showDelayedModal: true,
    })
  );
  yield put(doShowError("Erro ao logar. Tente novamente.", e));
}

// depois do requestLogin a sessao ja existe: qualquer falha do restante do fluxo
// mantem o cliente logado e o manda escolher o valor manualmente
function* fallbackToSelectNumber(e) {
  yield put(push("numero"));

  if (e) {
    yield put(doShowError("Não foi possível carregar os dados da recarga. Tente novamente.", e));
  }
}

// O passo seguinte ao login digisession depende do canal: a recarga monta a sessao
// de checkout a partir do offerId, o CLARO_YOUTUBE vai para a elegibilidade de ofertas.
function* rechargePostLogin({ data, customer, customerError, channelName, recipient }) {
  if (customerError) {
    yield* fallbackToSelectNumber(customerError);
    return;
  }

  const { offerId, ipAddress, tags } = data;

  try {
    yield put(doSaveCustomer(customer));

    const { data: paymentMethods } = yield call(fetchPaymentMethods);
    yield put(doSavePaymentMethods(paymentMethods));

    yield call(handlePriorityFetchFeature);
    yield putResolve(doFetchFeatures());

    const maintenance = yield select(state => isFeatureEnabled(state, "maintenance"));
    if (maintenance) {
      yield put(push("maintenance"));
      return;
    }

    if (!offerId) {
      yield* fallbackToSelectNumber();
      return;
    }

    let checkoutData;

    try {
      ({ data: checkoutData } = yield call(createCheckoutCoreSession, {
        msisdn: data.identifier,
        channel: channelName,
        recipient,
        offerId,
        ipAddress,
        tags,
      }));
    } catch (e) {
      // sem sessao de checkout o cliente segue pelo fluxo normal, sem erro de login
      yield* fallbackToSelectNumber();
      return;
    }

    yield put(doChangeNewRecharge("targetMsisdn", recipient));
    yield put(doSaveCheckoutSessionData({ checkoutData }));
    yield put(push("smartcheckout"));
  } catch (e) {
    yield* fallbackToSelectNumber(e);
  }
}

// CLARO_YOUTUBE: cliente indisponivel nao bloqueia a jornada, e o passo apos o login
// e sempre a elegibilidade de ofertas - nunca decide navegacao a partir de offerId.
// As tags de campanha ficam aqui, e nao no trecho compartilhado, para nao passarem a
// ser gravadas nos demais canais digisession.
function* claroYoutubePostLogin({ data, customer }) {
  if (data.tags?.utm_source) {
    sessionStorage.setItem("utmSource", data.tags.utm_source);
  }
  if (data.tags?.utm_content) {
    sessionStorage.setItem("utmContent", data.tags.utm_content);
  }
  if (data.tags?.utm_campaign) {
    sessionStorage.setItem("utmCampaign", data.tags.utm_campaign);
  }
  if (data.tags?.utm_medium) {
    sessionStorage.setItem("utmMedium", data.tags.utm_medium);
  }
  if (data.offerId) {
    sessionStorage.setItem("offerId", data.offerId);
  }

  if (customer) {
    yield put(doSaveCustomer(customer));
  }

  yield put(doFetchOfferCatalog());
  yield put(push(menuPaths.OFFERS));
}

const postLoginStrategies = {
  [channels.claro_youtube]: claroYoutubePostLogin,
};

function* handleRequestDigisessionLogin(action) {
  const { payload } = action;

  let data;
  let channelPath;
  let channelName;

  try {
    yield* resetLoginState();

    [channelPath, channelName] = payload.channel;

    sessionStorage.setItem("channelPath", channelPath);
    sessionStorage.setItem("channel", channelName);
    sessionStorage.removeItem("origin");
    sessionStorage.removeItem("rechargeValue");

    ({ data } = yield call(requestLogin, payload));
  } catch (e) {
    yield* failDigisessionAuth(e);
    return;
  }

  const recipient = data.recipient || data.identifier;
  const channel = data.portal || channelName;

  storage.setItem("identifier", data.identifier);
  sessionStorage.setItem("token", data.id);
  sessionStorage.setItem("partnerExternalId", data.partnerExternalId);
  sessionStorage.setItem("channel", channel);
  setDigisessionSession(data.id);
  setSessionLoopId(data.tags?.session_loop_id);

  let customer = null;
  let customerError = null;

  try {
    ({ data: customer } = yield call(fetchCustomer, data.identifier));
  } catch (e) {
    customerError = e;
  }

  const segment = data.segment || customer?.segment || "";
  storage.setItem("segment", segment);

  yield put(
    doSuccessLogin({
      token: data.id,
      partnerExternalId: data.partnerExternalId,
      identifier: data.identifier,
      recipient,
      segment,
      channel,
      channelPath,
    })
  );

  const runPostLogin = postLoginStrategies[channelName] || rechargePostLogin;
  yield* runPostLogin({ data, customer, customerError, channelName, recipient });
}

function* handleRequestLogout() {
  yield put(doHideModal());

  logout();

  yield put(doResetCustomer());
  yield put(doResetNewRecharge());
  yield put(doResetOfferCatalog());
  yield put(push(`../${sessionStorage.getItem("channelPath")}`));
}

export {
  handleRequestSMS,
  handleRequestResendSMS,
  handleRequestLogin,
  handleRequestLoginDeviceToken,
  handleRequestLogout,
  handleRequestCpfLogin,
  handleRequestEncryptedLogin,
  handleRequestDigisessionLogin,
};
